/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Box, Skeleton } from "@mui/material";
import { ScrollingCarousel } from "@trendyol-js/react-carousel";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";

import StoreCard from "./StoreCard";

const BrandCarousel = ({
  classes,
  barndList,
  isBrandLoading,
  brandCount,
  selBrand,
  brandSvgs,
  selCategory,
  selCountry,
  selState,
  selCity,
  selCounty,
  userDetails,
  drawerContentRoute,
  showDrawer,
  onBrandSelect,
  onFavouriteToggle,
}) => {
  const wrapperRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(true);

  useEffect(() => {
    const check = () => {
      // Use :last-child to target the actual scroll div, not arrow overlay divs
      const inner = wrapperRef.current?.querySelector(
        ".scrollingCarousal > div:last-child",
      );
      if (inner) setIsOverflowing(inner.scrollWidth > inner.clientWidth);
    };
    check();
    const ro = new ResizeObserver(check);
    if (wrapperRef.current) ro.observe(wrapperRef.current);
    return () => ro.disconnect();
  }, [barndList]);

  return (
    <div
      css={classes.brandCardsWrapper}
      ref={wrapperRef}
      style={{ "--cj": isOverflowing ? "flex-start" : "center" }}
    >
      {isBrandLoading && (
        <Box
          sx={{
            display: "flex",
            gap: "12px",
            px: 2,
            py: 1,
            overflowX: "hidden",
            justifyContent: "center",
            width: "100%",
          }}
        >
          {Array.from({ length: 5 }).map((_, i) => (
            <Box
              key={i}
              sx={{
                flexShrink: 0,
                width: 165,
                borderRadius: "14px",
                overflow: "hidden",
                background: "#fff",
                border: "1px solid rgba(0,0,0,0.09)",
                boxShadow: "0 3px 14px rgba(0,0,0,0.1)",
                position: "relative",
              }}
            >
              {/* Store header skeleton — matches storeHeader padding: 16px 12px 58px ≈ 90px */}
              <Skeleton
                variant="rectangular"
                width={165}
                height={90}
                sx={{ bgcolor: "rgba(46,125,50,0.18)", display: "block" }}
              />
              {/* Fav button inside header top-left */}
              <Box sx={{ position: "absolute", top: 10, left: 10 }}>
                <Skeleton
                  variant="circular"
                  width={24}
                  height={24}
                  sx={{ bgcolor: "rgba(255,255,255,0.4)" }}
                />
              </Box>
              {/* Circular logo skeleton — marginTop: -62px overlapping header */}
              <Box
                sx={{
                  position: "absolute",
                  left: "50%",
                  transform: "translateX(-50%)",
                  top: 28,
                  width: 124,
                  height: 124,
                  borderRadius: "50%",
                  border: "3px solid #fff",
                  boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
                  background: "#f1f8f0",
                  zIndex: 2,
                  overflow: "hidden",
                }}
              >
                <Skeleton
                  variant="circular"
                  width={124}
                  height={124}
                  sx={{ bgcolor: "rgba(0,0,0,0.07)" }}
                />
              </Box>
              {/* Store body — padding-top clears the circle (28 + 124 - 90 = 62px + 8px gap) */}
              <Box
                sx={{
                  paddingTop: "70px",
                  padding: "70px 10px 12px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "8px",
                  background: "#fafafa",
                }}
              >
                {/* Company name */}
                <Skeleton
                  variant="rounded"
                  width="65%"
                  height={13}
                  sx={{ bgcolor: "rgba(0,0,0,0.08)" }}
                />
                {/* Phone + map action buttons */}
                <Box
                  sx={{
                    display: "flex",
                    gap: "10px",
                    justifyContent: "center",
                    mt: 0.5,
                  }}
                >
                  <Skeleton
                    variant="circular"
                    width={30}
                    height={30}
                    sx={{ bgcolor: "rgba(84,172,52,0.12)" }}
                  />
                  <Skeleton
                    variant="circular"
                    width={30}
                    height={30}
                    sx={{ bgcolor: "rgba(84,172,52,0.12)" }}
                  />
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {!isBrandLoading && barndList?.length > 0 && (
        <ScrollingCarousel className="scrollingCarousal">
          {barndList.map((item) => {
            if (!item?.is_active) return null;
            return (
              <StoreCard
                key={item.uuid}
                item={item}
                selBrand={selBrand}
                classes={classes}
                onBrandSelect={onBrandSelect}
                onFavouriteToggle={onFavouriteToggle}
              />
            );
          })}
        </ScrollingCarousel>
      )}

      {brandCount === 0 && !isBrandLoading && (
        <div css={classes.noResult}>
          <div css={classes.noResultIcon}>
            <StorefrontOutlinedIcon sx={{ fontSize: "1.5rem" }} />
          </div>
          <p css={classes.noResultTitle}>Coming Soon</p>
          <p css={classes.noResultSub}>
            No stores are available in your area yet.
          </p>
        </div>
      )}
    </div>
  );
};

BrandCarousel.propTypes = {
  classes: PropTypes.object.isRequired,
  barndList: PropTypes.array,
  isBrandLoading: PropTypes.bool,
  brandCount: PropTypes.number,
  selBrand: PropTypes.array,
  brandSvgs: PropTypes.array.isRequired,
  selCategory: PropTypes.string,
  selCountry: PropTypes.string,
  selState: PropTypes.string,
  selCity: PropTypes.string,
  selCounty: PropTypes.string,
  userDetails: PropTypes.object,
  drawerContentRoute: PropTypes.string,
  showDrawer: PropTypes.bool,
  onBrandSelect: PropTypes.func.isRequired,
  onFavouriteToggle: PropTypes.func.isRequired,
};

export default BrandCarousel;
